import { cn } from '@repo/shared-client/lib/utils'
import { isNonProductionEnvironment } from '@web/src/lib/app-environment'
import { useTranslation } from '@web/src/lib/intl/react'
import { RefreshCwIcon } from 'lucide-react'
import React, { createContext, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { usePwa } from './pwa-context'

const isBrowser = typeof window !== 'undefined'

type PullToRefreshContextType = {
  isRefreshing: boolean
  lastRefreshed: Date | null
  refreshPage: () => void
  setDisabled: (disabled: boolean) => void
  isDisabled: boolean
}

const PullToRefreshContext = createContext<PullToRefreshContextType>({
  isRefreshing: false,
  lastRefreshed: null,
  refreshPage: () => {
    /* noop */
  },
  setDisabled: () => {
    /* noop */
  },
  isDisabled: false,
})

export const PullToRefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)
  const [pullDistance, setPullDistance] = useState(0)
  const [pullIconDistance, setPullIconDistance] = useState(0)
  const [isPulling, setIsPulling] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const { isIOS, isInstalled } = usePwa()
  const minSwipeDistance = 150 // Minimum distance required for a swipe

  // Debug flag to enable pull-to-refresh on non-iOS devices
  const debugMode = isNonProductionEnvironment()

  const setDisabled = (disabled: boolean) => {
    setIsDisabled(disabled)
    // Reset pull state when disabling
    if (disabled) {
      setIsPulling(false)
      setPullDistance(0)
      setPullIconDistance(0)
    }
  }

  // Helper function to check if any scrollable element under the touch point has been scrolled
  const isAnyScrollableElementScrolled = (x: number, y: number): boolean => {
    if (!isBrowser) {
      return false
    }

    // Get all elements at the touch point
    const elementsAtPoint = document.elementsFromPoint(x, y)

    for (const element of elementsAtPoint) {
      // Check if element is scrollable
      const computedStyle = window.getComputedStyle(element)
      const overflowY = computedStyle.overflowY
      const overflowX = computedStyle.overflowX

      // Check for reversed flex direction - disable pull-to-refresh if found
      const flexDirection = computedStyle.flexDirection
      if (flexDirection === 'column-reverse' || flexDirection === 'row-reverse') {
        return true
      }

      // Check if element has scrollable overflow
      const isScrollableY = overflowY === 'scroll' || overflowY === 'auto'
      const isScrollableX = overflowX === 'scroll' || overflowX === 'auto'

      if (isScrollableY || isScrollableX) {
        // Check if the element is actually scrolled
        if (element.scrollTop > 0 || element.scrollLeft > 0) {
          return true
        }

        // Also check if the element can be scrolled (has overflow content)
        const hasVerticalScroll = element.scrollHeight > element.clientHeight
        const hasHorizontalScroll = element.scrollWidth > element.clientWidth

        // If it's scrollable and has content that can be scrolled, but is at the top,
        // we still want to prevent pull-to-refresh if the user might be trying to scroll this element
        if ((hasVerticalScroll && isScrollableY) || (hasHorizontalScroll && isScrollableX)) {
          // Additional check: if element has scrollable content and is not at the very top of its scroll
          if (element.scrollTop > 0) {
            return true
          }
        }
      }
    }

    return false
  }

  // Enhanced function to check if we're truly at the top and not interacting with scrollable content
  const isTrulyAtTop = (touchX: number, touchY: number): boolean => {
    if (!isBrowser) {
      return false
    }

    // First check if window is at top
    const windowAtTop = window.scrollY <= 0

    if (!windowAtTop) {
      return false
    }

    // Then check if any scrollable elements under the touch point are scrolled
    const hasScrolledElements = isAnyScrollableElementScrolled(touchX, touchY)

    return !hasScrolledElements
  }

  const refreshPage = () => {
    if (!isBrowser) {
      return
    }

    setIsRefreshing(true)
    setIsPulling(false)
    setPullDistance(0)

    // Reload the page
    window.location.reload()

    // Update last refreshed timestamp
    const now = new Date()
    setLastRefreshed(now)
    setIsRefreshing(false)
  }

  useEffect(() => {
    // Enable pull-to-refresh for iOS PWA or when debug mode is enabled
    // But disable if isDisabled is true or not in browser
    if (!(isBrowser && ((isIOS && isInstalled) || debugMode)) || isDisabled) {
      return
    }

    let touchStartY = 0
    let touchStartX = 0
    let touchEndY = 0
    const maxPullDistance = window.innerHeight / 6 // Maximum 1/6 of screen height
    let isAtTop = false
    let isMultiTouch = false

    const resetPullState = () => {
      setIsPulling(false)
      setPullDistance(0)
      setPullIconDistance(0)
    }

    const handleTouchStart = (e: TouchEvent) => {
      // Check if this is a multi-touch gesture
      if (e.touches.length > 1) {
        isMultiTouch = true
        resetPullState()
        return
      }

      isMultiTouch = false
      touchStartY = e.touches[0].clientY
      touchStartX = e.touches[0].clientX

      // Check if we're truly at the top (considering scrollable child elements)
      isAtTop = isTrulyAtTop(touchStartX, touchStartY)
    }

    const handleTouchMove = (e: TouchEvent) => {
      // Cancel pull-to-refresh if multi-touch is detected
      if (e.touches.length > 1) {
        isMultiTouch = true
        resetPullState()
        return
      }

      // Don't proceed if we detected multi-touch earlier or not at top
      if (isMultiTouch || !isAtTop) {
        return
      }

      touchEndY = e.touches[0].clientY
      const distance = touchEndY - touchStartY

      if (distance > 0) {
        // Prevent default scrolling behavior when pulling down
        e.preventDefault()

        // Calculate pull distance with diminishing returns for smoother feel
        const dampedDistance = Math.min(distance * 0.5, maxPullDistance)
        setPullIconDistance(dampedDistance)
        setPullDistance(distance)
        setIsPulling(true)
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      // Check if there are still active touches (multi-touch scenario)
      if (e.touches.length > 0) {
        // Still have active touches, don't process the end event yet
        return
      }

      // Don't trigger refresh if this was a multi-touch gesture
      if (isMultiTouch) {
        resetPullState()
        touchStartY = 0
        touchStartX = 0
        touchEndY = 0
        isMultiTouch = false
        return
      }

      const distance = touchEndY - touchStartY

      // If pulled down far enough and we're at the top
      if (distance > minSwipeDistance && isAtTop) {
        refreshPage()
        toast.success(t('Refreshing the page'))
      } else {
        // Reset pull state if not refreshing
        resetPullState()
      }

      // Reset values
      touchStartY = 0
      touchStartX = 0
      touchEndY = 0
      isMultiTouch = false
    }

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: false })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd, { passive: false })

    // Cleanup
    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIOS, isInstalled, debugMode, isDisabled])

  // Calculate icon opacity based on pull distance
  const iconOpacity = pullDistance > minSwipeDistance ? 1 : pullDistance / (minSwipeDistance + minSwipeDistance * 0.5)

  // Calculate rotation based on damped distance (pullIconDistance)
  const maxPullDistance = isBrowser ? window.innerHeight / 6 : 0
  const rotationDegrees = (pullIconDistance / maxPullDistance) * 360 // Full rotation at max pull

  return (
    <PullToRefreshContext.Provider
      value={{
        isRefreshing,
        lastRefreshed,
        refreshPage,
        setDisabled,
        isDisabled,
      }}
    >
      {/* Pull-to-refresh visual indicator */}
      {isPulling && !isDisabled && (
        <div
          className="pointer-events-none fixed top-0 left-1/2 z-max rounded-lg border border-border bg-popover p-2 shadow-md transition-opacity duration-200 ease-out"
          style={{
            opacity: iconOpacity,
            transform: `translateX(-50%) translateY(${pullIconDistance}px)`,
          }}
        >
          <RefreshCwIcon
            className={cn(
              'drop-shadow-md transition-transform duration-100 ease-out',
              pullDistance > minSwipeDistance ? 'text-primary' : 'text-muted-foreground'
            )}
            size={32}
            strokeWidth={pullDistance > minSwipeDistance ? 3 : 1.5}
            style={{
              transform: `rotate(${rotationDegrees}deg)`,
            }}
          />
        </div>
      )}

      {children}
    </PullToRefreshContext.Provider>
  )
}

export const usePullToRefresh = () => useContext(PullToRefreshContext)
